import type { SceneObject } from "../../SceneHost";
import { placeHtmlObject } from "../htmlLayerObject";


export type TimerAction = "start" | "pause" | "reset" | "finish" | "setDuration";

export type TimerRenderOpts = {
  canEdit?: boolean;
  onTimerAction?: (objectId: string, action: TimerAction, payload?: { durationMs?: number }) => void;
  onRequestSelect?: (objectId: string) => void;
};

const tickers = new Map<string, number>();
const finishArmed = new Map<string, boolean>();
const beepPlayedForEndsAt = new Map<string, number>();

function clearTicker(objectId: string) {
  const id = tickers.get(objectId);
  if (id != null) window.clearInterval(id);
  tickers.delete(objectId);
}

/**
 * Снимает интервалы таймеров, которых больше нет на доске.
 *
 * clearTicker вызывается при перерисовке объекта, но удалённый объект больше
 * не перерисовывается никогда — его интервал продолжал тикать до перезагрузки
 * страницы, дёргая уже отсоединённый DOM. Три модульные Map при этом росли.
 */
export function disposeTimersExcept(activeIds: Set<string>): void {
  for (const id of [...tickers.keys()]) {
    if (!activeIds.has(id)) clearTicker(id);
  }
  for (const id of [...finishArmed.keys()]) {
    if (!activeIds.has(id)) finishArmed.delete(id);
  }
  for (const id of [...beepPlayedForEndsAt.keys()]) {
    if (!activeIds.has(id)) beepPlayedForEndsAt.delete(id);
  }
}

/** Полная остановка — при закрытии доски. */
export function disposeAllTimers(): void {
  disposeTimersExcept(new Set());
}

function formatMs(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function readTimerFields(obj: SceneObject) {
  const durationMs = typeof obj.durationMs === "number" ? obj.durationMs : 5 * 60 * 1000;
  const remainingMs = typeof obj.remainingMs === "number" ? obj.remainingMs : durationMs;
  const status =
    obj.status === "running" ||
    obj.status === "paused" ||
    obj.status === "finished" ||
    obj.status === "idle"
      ? obj.status
      : "idle";
  const endsAt = typeof obj.endsAt === "number" ? obj.endsAt : undefined;
  return { durationMs, remainingMs, status, endsAt };
}

function remainingNow(obj: SceneObject): number {
  const { remainingMs, status, endsAt } = readTimerFields(obj);
  if (status === "running" && endsAt != null) {
    return Math.max(0, endsAt - Date.now());
  }
  return Math.max(0, remainingMs);
}

function playEndBeep() {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.08;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.stop(ctx.currentTime + 0.4);
    window.setTimeout(() => void ctx.close(), 500);
  } catch {
    /* autoplay / unsupported */
  }
}

function stopProp(el: Element) {
  for (const type of ["pointerdown", "mousedown", "click", "dblclick"] as const) {
    el.addEventListener(type, (ev) => ev.stopPropagation());
  }
}

const PRESETS_MS = [
  { label: "1м", ms: 60_000 },
  { label: "3м", ms: 3 * 60_000 },
  { label: "5м", ms: 5 * 60_000 },
  { label: "10м", ms: 10 * 60_000 },
] as const;

const DURATION_MIN_MS = 1000;
const DURATION_MAX_MS = 24 * 60 * 60 * 1000;

function clampDurationMs(ms: number): number {
  if (!Number.isFinite(ms)) return 60_000;
  return Math.max(DURATION_MIN_MS, Math.min(DURATION_MAX_MS, Math.round(ms)));
}

function durationParts(ms: number): { m: number; s: number } {
  const total = Math.max(0, Math.round(ms / 1000));
  return { m: Math.floor(total / 60), s: total % 60 };
}

export function renderTimerObject(obj: SceneObject, opts: TimerRenderOpts = {}): HTMLElement {
  const canEdit = Boolean(opts.canEdit);
  const w = typeof obj.w === "number" ? obj.w : 300;
  const h = typeof obj.h === "number" ? obj.h : 248;
  const fields = readTimerFields(obj);

  clearTicker(obj.id);

  const g = document.createElement("div");
  g.setAttribute("class", "wb-scene-obj wb-scene-obj--timer");
  g.setAttribute("data-object-id", obj.id);
  placeHtmlObject(g, obj, w, h);

  const card = document.createElement("div");
  card.style.width = "100%";
  card.style.height = "100%";
  card.style.boxSizing = "border-box";
  card.setAttribute(
    "class",
    `wb-timer-card${fields.status === "finished" ? " is-finished" : ""}${
      fields.status === "running" ? " is-running" : ""
    }${fields.status === "paused" ? " is-paused" : ""}`
  );
  card.setAttribute("data-object-id", obj.id);
  stopProp(card);

  const head = document.createElement("div");
  head.setAttribute("class", "wb-timer-card__head");

  const title = document.createElement("div");
  title.setAttribute("class", "wb-timer-card__title");
  title.textContent = "Таймер";
  head.appendChild(title);

  const editBtn = document.createElement("button") as HTMLButtonElement;
  editBtn.type = "button";
  editBtn.setAttribute("class", "wb-timer-card__edit");
  editBtn.setAttribute("data-object-id", obj.id);
  editBtn.title = "Выделить для перемещения, размера, комментария";
  editBtn.textContent = "Передвинуть";
  if (canEdit && opts.onRequestSelect) {
    const selectSelf = (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
      opts.onRequestSelect?.(obj.id);
    };
    // pointerdown: надёжнее click (selectTool в capture / рамка выделения).
    editBtn.addEventListener("pointerdown", selectSelf);
    editBtn.addEventListener("click", selectSelf);
  } else {
    editBtn.disabled = true;
  }
  head.appendChild(editBtn);
  card.appendChild(head);

  const timeEl = document.createElement("div");
  timeEl.setAttribute("class", "wb-timer-card__time");
  timeEl.textContent = formatMs(remainingNow(obj));

  const display = document.createElement("div");
  display.setAttribute("class", "wb-timer-card__display");
  display.appendChild(timeEl);

  const progress = document.createElement("div");
  progress.setAttribute("class", "wb-timer-card__progress");
  progress.setAttribute("aria-hidden", "true");
  const progressFill = document.createElement("div");
  progressFill.setAttribute("class", "wb-timer-card__progress-fill");
  progress.appendChild(progressFill);
  display.appendChild(progress);

  const statusEl = document.createElement("div");
  statusEl.setAttribute("class", "wb-timer-card__status");
  if (fields.status === "running") statusEl.textContent = "идёт";
  else if (fields.status === "paused") statusEl.textContent = "пауза";
  else if (fields.status === "finished") statusEl.textContent = "готово";
  else statusEl.textContent = "ожидание";
  display.appendChild(statusEl);

  card.appendChild(display);

  if (canEdit && (fields.status === "idle" || fields.status === "paused" || fields.status === "finished")) {
    const presets = document.createElement("div");
    presets.setAttribute("class", "wb-timer-card__presets");
    const isPreset = PRESETS_MS.some((p) => p.ms === fields.durationMs);
    for (const p of PRESETS_MS) {
      const btn = document.createElement("button") as HTMLButtonElement;
      btn.type = "button";
      btn.className = `wb-timer-card__preset${fields.durationMs === p.ms ? " is-active" : ""}`;
      btn.textContent = p.label;
      btn.title = `Длительность ${p.label}`;
      btn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        opts.onTimerAction?.(obj.id, "setDuration", { durationMs: p.ms });
      });
      presets.appendChild(btn);
    }
    card.appendChild(presets);

    const custom = document.createElement("div");
    custom.setAttribute("class", "wb-timer-card__custom");
    const parts = durationParts(fields.durationMs);

    const minInput = document.createElement("input") as HTMLInputElement;
    minInput.type = "text";
    minInput.inputMode = "numeric";
    minInput.autocomplete = "off";
    minInput.maxLength = 4;
    minInput.className = "wb-timer-card__time-input";
    minInput.setAttribute("aria-label", "Минуты");
    minInput.title = "Минуты";
    minInput.placeholder = "мм";
    minInput.spellcheck = false;
    minInput.value = String(parts.m);

    const sep = document.createElement("span");
    sep.className = "wb-timer-card__custom-sep";
    sep.textContent = ":";

    const secInput = document.createElement("input") as HTMLInputElement;
    secInput.type = "text";
    secInput.inputMode = "numeric";
    secInput.autocomplete = "off";
    secInput.maxLength = 2;
    secInput.className = "wb-timer-card__time-input";
    secInput.setAttribute("aria-label", "Секунды");
    secInput.title = "Секунды";
    secInput.placeholder = "сс";
    secInput.spellcheck = false;
    secInput.value = String(parts.s).padStart(2, "0");

    let sent = false;
    function commitCustom() {
      const m = Math.max(0, Math.min(24 * 60, Math.floor(Number.parseInt(minInput.value, 10) || 0)));
      let s = Math.max(0, Math.floor(Number.parseInt(secInput.value, 10) || 0));
      if (s > 59) s = 59;
      const next = clampDurationMs((m * 60 + s) * 1000);
      minInput.value = String(durationParts(next).m);
      secInput.value = String(durationParts(next).s).padStart(2, "0");
      if (sent || next === fields.durationMs) return;
      sent = true;
      opts.onTimerAction?.(obj.id, "setDuration", { durationMs: next });
    }

    for (const input of [minInput, secInput]) {
      input.addEventListener("keydown", (ev) => {
        ev.stopPropagation();
        if (ev.key === "Enter") {
          ev.preventDefault();
          commitCustom();
          (ev.target as HTMLInputElement).blur();
        }
      });
      input.addEventListener("pointerdown", (ev) => ev.stopPropagation());
      input.addEventListener("mousedown", (ev) => ev.stopPropagation());
      input.addEventListener("focus", () => input.select());
    }

    custom.addEventListener("focusout", (ev) => {
      const next = ev.relatedTarget as Node | null;
      if (next && custom.contains(next)) return;
      commitCustom();
    });

    if (!isPreset) {
      custom.classList.add("is-active");
    }
    custom.appendChild(minInput);
    custom.appendChild(sep);
    custom.appendChild(secInput);
    card.appendChild(custom);
  }

  if (canEdit) {
    const actions = document.createElement("div");
    actions.setAttribute("class", "wb-timer-card__actions");

    const primary = document.createElement("button") as HTMLButtonElement;
    primary.type = "button";
    primary.className = "wb-timer-card__btn wb-timer-card__btn--primary";
    if (fields.status === "running") {
      primary.textContent = "Пауза";
      primary.addEventListener("click", (ev) => {
        ev.stopPropagation();
        opts.onTimerAction?.(obj.id, "pause");
      });
    } else {
      primary.textContent = fields.status === "finished" ? "Ещё раз" : "Старт";
      primary.addEventListener("click", (ev) => {
        ev.stopPropagation();
        opts.onTimerAction?.(obj.id, "start");
      });
    }
    actions.appendChild(primary);

    if (fields.status === "running" || fields.status === "paused") {
      const reset = document.createElement("button") as HTMLButtonElement;
      reset.type = "button";
      reset.className = "wb-timer-card__btn";
      reset.textContent = "Сброс";
      reset.addEventListener("click", (ev) => {
        ev.stopPropagation();
        opts.onTimerAction?.(obj.id, "reset");
      });
      actions.appendChild(reset);
    }

    card.appendChild(actions);
  }

  g.appendChild(card);

  const paint = () => {
    const rem = remainingNow(obj);
    timeEl.textContent = formatMs(rem);
    const dur = Math.max(1, fields.durationMs);
    const ratio = Math.max(0, Math.min(1, rem / dur));
    progressFill.style.transform = `scaleX(${ratio})`;
    card.style.setProperty("--wb-timer-p", String(ratio));
    if (fields.status === "running" && rem <= 0) {
      clearTicker(obj.id);
      const endsKey = fields.endsAt ?? 0;
      if (beepPlayedForEndsAt.get(obj.id) !== endsKey) {
        beepPlayedForEndsAt.set(obj.id, endsKey);
        playEndBeep();
      }
      if (!finishArmed.get(obj.id)) {
        finishArmed.set(obj.id, true);
        opts.onTimerAction?.(obj.id, "finish");
      }
    }
  };

  paint();

  if (fields.status === "running") {
    finishArmed.set(obj.id, false);
    tickers.set(
      obj.id,
      window.setInterval(() => {
        paint();
      }, 200)
    );
  } else {
    finishArmed.delete(obj.id);
  }

  return g;
}
